import type {
  AbortMultipartUploadBody,
  AbortMultipartUploadResponse,
  CompleteMultipartUploadBody,
  CompleteMultipartUploadResponse,
  GetPresignedUrlsForPartsBody,
  GetPresignedUrlsForPartsResponse,
  InitiateMultipartUploadBody,
  InitiateMultipartUploadResponse,
} from "@nlc/shared-types";

import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import axios, { type AxiosResponse } from "axios";

import authenticatedApi from "@/lib/api/client";

const exponentialBackoffDelay = (
  attemptIndex: number,
  _error: Error,
): number => {
  const baseDelay = 1000;
  const maxDelay = 30_000;

  const jitter = 0.25;
  const randomMultiplier = 1 + (Math.random() - 0.5) * 2 * jitter;

  const exponentialDelay = baseDelay * Math.pow(2, attemptIndex);
  const delayWithJitter = exponentialDelay * randomMultiplier;

  return Math.min(delayWithJitter, maxDelay);
};

export interface UploadChunkParams {
  chunk: Blob;
  onUploadProgress?: (progressEvent: {
    loaded: number;
    total?: number;
  }) => void;
  presignedUrl: string;
  signal?: AbortSignal;
}

export function useMultipartUploadMutations(): {
  abortMultipartUploadMutation: UseMutationResult<
    AbortMultipartUploadResponse,
    Error,
    AbortMultipartUploadBody,
    unknown
  >;
  completeMultipartUploadMutation: UseMutationResult<
    CompleteMultipartUploadResponse,
    Error,
    CompleteMultipartUploadBody,
    unknown
  >;
  getPresignedUrlsForPartsMutation: UseMutationResult<
    GetPresignedUrlsForPartsResponse,
    Error,
    GetPresignedUrlsForPartsBody,
    unknown
  >;
  initiateMultipartUploadMutation: UseMutationResult<
    InitiateMultipartUploadResponse,
    Error,
    InitiateMultipartUploadBody,
    unknown
  >;
  uploadChunkMutation: UseMutationResult<
    AxiosResponse,
    Error,
    UploadChunkParams,
    unknown
  >;
} {
  const initiateMultipartUploadMutation = useMutation({
    mutationFn: async (body: InitiateMultipartUploadBody) =>
      await authenticatedApi
        .url("/my-files/multipart/initiate")
        .post(body)
        .json<InitiateMultipartUploadResponse>(),
  });

  const getPresignedUrlsForPartsMutation = useMutation({
    mutationFn: async (body: GetPresignedUrlsForPartsBody) =>
      await authenticatedApi
        .url("/my-files/multipart/presigned-urls")
        .post(body)
        .json<GetPresignedUrlsForPartsResponse>(),
  });

  const completeMultipartUploadMutation = useMutation({
    mutationFn: async (body: CompleteMultipartUploadBody) =>
      await authenticatedApi
        .url("/my-files/multipart/complete")
        .post(body)
        .json<CompleteMultipartUploadResponse>(),
  });

  const abortMultipartUploadMutation = useMutation({
    mutationFn: async (body: AbortMultipartUploadBody) =>
      await authenticatedApi
        .url("/my-files/multipart/abort")
        .post(body)
        .json<AbortMultipartUploadResponse>(),
  });

  const uploadChunkMutation = useMutation({
    mutationFn: async ({
      chunk,
      onUploadProgress,
      presignedUrl,
      signal,
    }: UploadChunkParams) =>
      await axios.put(presignedUrl, chunk, {
        onUploadProgress,
        signal,
      }),
    retry: 5,
    retryDelay: exponentialBackoffDelay,
  });

  return {
    abortMultipartUploadMutation,
    completeMultipartUploadMutation,
    getPresignedUrlsForPartsMutation,
    initiateMultipartUploadMutation,
    uploadChunkMutation,
  };
}
